import { faChevronRight, faPlusCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { take } from "lodash";
import { parseAsBoolean, useQueryState } from "nuqs";
import { Link } from "react-router-dom";
import { Button, Input, Segment } from "semantic-ui-react";
import Carousel from "../../../../../../../components/Carousel";
import { useDebounce } from "../../../../../../../hooks/useDebounce";
import useSite from "../../../../../../permits/components/inspections/InspectionPanel/useSite";
import { NotifyTopNav } from "../../../../../components/NotifyTopNav";
import { useInfiniteNotificationContent } from "../../../../../hooks/useInfiniteNotificationContent";
import { NotifyNotificationsTemplateCardInfiniteList } from "./components/NotifyNotificationsTemplateCardInfiniteList";
import { NotifyNotificationsTemplates } from "./constants/NotifyNotificationsTemplates";

export function NotifyNotificationsCreateFromTemplatePage() {
  const site = useSite();

  const [queryFilter, setQueryFilter] = useQueryState("query", { defaultValue: "" });
  const debouncedQueryFilter = useDebounce(queryFilter, 300);
  const [seeMore, setSeeMore] = useQueryState("see-more", parseAsBoolean.withDefault(false));

  const { total, isNotificationContentFetching } = useInfiniteNotificationContent({
    take: 50,
    query: queryFilter,
    departmentGroupId: null,
    status: null,
  });

  const filteredTemplates = NotifyNotificationsTemplates.filter(({ card }) =>
    card.title.toLowerCase().includes(queryFilter.toLowerCase()),
  );

  const isLoading = isNotificationContentFetching || queryFilter !== debouncedQueryFilter;

  return (
    <div className='tw-relative'>
      <div className='tw-sticky tw-top-0 tw-z-50'>
        <NotifyTopNav />
        <Segment attached='bottom' className='tw-mt-0 tw-rounded-none tw-border-x-0 tw-p-7 tw-shadow-md'>
          <div className='tw-flex tw-flex-col-reverse tw-justify-between tw-gap-3 md:tw-flex-row md:tw-items-center'>
            <div className='tw-flex tw-flex-col tw-gap-3 lg:tw-flex-row lg:tw-items-center'>
              <Input
                className='md:tw-w-[300px]'
                value={queryFilter}
                onChange={(_, { value }) => setQueryFilter(value)}
                placeholder='Search Posts'
                icon='search'
                iconPosition='left'
                loading={isLoading}
              />
              <span
                className={classNames("tw-text-text-secondary", {
                  "tw-hidden": queryFilter.length <= 0,
                })}
              >
                Search results {'"'}
                {queryFilter}
                {'"'} ({total} previous notifications and {filteredTemplates.length} templates)
              </span>
            </div>
            <Button
              as={Link}
              to={`/${site}/notify/notifications/create`}
              primary
              className='tw-m-0 tw-space-x-2 tw-whitespace-nowrap'
            >
              <FontAwesomeIcon icon={faPlusCircle} />
              <span>Blank Notification</span>
            </Button>
          </div>
        </Segment>
      </div>

      <Segment className='tw-m-7 tw-border-none tw-p-7'>
        <div className='tw-mb-3 tw-flex tw-items-center tw-justify-between'>
          <h2 className='tw-m-0 tw-text-[24px] tw-font-bold'>Templates</h2>
          <Button className='button-ghost md:tw-hidden' onClick={() => setSeeMore((prev) => !prev)}>
            {seeMore ? "See Less" : "See More"}
          </Button>
        </div>
        {filteredTemplates.length === 0 && <span className='tw-text-text-secondary'>No templates found</span>}
        <div className='tw-hidden md:tw-block'>
          <Carousel options={{ align: "start", dragFree: true }}>
            {filteredTemplates.map(({ id, card }) => {
              const templateSearchParams = new URLSearchParams();
              templateSearchParams.set("template-type", "template");
              templateSearchParams.set("template-id", `${id}`);
              return (
                <Link
                  key={id}
                  to={{
                    pathname: `/${site}/notify/notifications/create`,
                    search: `?${templateSearchParams.toString()}`,
                  }}
                >
                  <div className='tw-group tw-mr-3 tw-flex tw-cursor-pointer tw-flex-col tw-gap-2 tw-text-text'>
                    <div className='tw-flex tw-h-36 tw-w-52 tw-items-center tw-justify-center tw-rounded-md tw-bg-white-background group-hover:tw-bg-white-dark'>
                      <card.icon />
                    </div>
                    <span className='tw-select-none tw-text-lg tw-font-bold'>{card.title}</span>
                  </div>
                </Link>
              );
            })}
          </Carousel>
        </div>
        <div className='md:tw-hidden'>
          {take(filteredTemplates, seeMore ? filteredTemplates.length : 3).map(({ id, card }) => {
            const templateSearchParams = new URLSearchParams();
            templateSearchParams.set("template-type", "template");
            templateSearchParams.set("template-id", `${id}`);
            return (
              <Link
                key={id}
                to={{
                  pathname: `/${site}/notify/notifications/create`,
                  search: `?${templateSearchParams.toString()}`,
                }}
              >
                <div className='tw-flex tw-items-center tw-justify-between tw-rounded-md tw-p-3 hover:tw-bg-white-background'>
                  <div className='tw-group tw-flex tw-cursor-pointer tw-gap-2 tw-text-text'>
                    <card.icon />
                    <span className='tw-select-none tw-text-lg tw-font-bold'>{card.title}</span>
                  </div>
                  <FontAwesomeIcon icon={faChevronRight} className='tw-px-3' />
                </div>
              </Link>
            );
          })}
        </div>
        <h2 className='tw-text-[24px] tw-font-bold'>Previous Notifications</h2>
        {total === 0 && <span className='tw-text-text-secondary'>No previous notifications found</span>}
        <NotifyNotificationsTemplateCardInfiniteList query={queryFilter} />
      </Segment>
    </div>
  );
}
